import React from 'react';
import { throttle } from 'lodash';
import moment from 'moment-timezone';
import { addDays } from 'date-fns';
import CoachingConstants, {
  CoachingSessionFrequency,
  CoachingSessionDuration,
} from 'coaching/CoachingConstants';
import Coin from 'components/Coin';
import Columns2 from 'components/Columns2';
import CoachingCoordinatorAvailabilityConflicts from './CoachingCoordinatorAvailabilityConflicts';
import CoachingCoordinatorCoachSelect from './CoachingCoordinatorCoachSelect';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import DatePickerInput from 'components/DatePickerInput';
import EventTracking from 'lib/EventTracking';
import { getCoachAvailabilityForEvent } from 'coaching/actions/CoachingActions';
import Heading from 'components/Heading';
import Icon from 'components/Icon';
import Label from 'components/Label';
import MediaObject from 'components/MediaObject';
import Notice from 'components/Notice';
import Row from 'components/Row';
import Select from 'components/Select';
import Text from 'components/Text';
import TimePickerInput from 'components/TimePickerInput';
import ToastActions from 'actions/ToastActions';
import {
  ParticipantRow,
  CoachRow,
} from 'coaching/components/CoachingCoordinatorView/CoachingCoordinatorPeopleListView';
import { createCoachingSession } from 'coaching/actions/CoachingActions';
import { CoachingSessionInput, CoachingUserType } from 'globalTypes';
import { formatRecurrenceRules } from 'lib/dateTimeFormat';

import GET_COACHING_SESSIONS_QUERY from 'coaching/queries/GetCoachingSessions.gql';

// The maximum number of future instances which can be unavailable for a recurring session.
// If more than this # are unavailable, we don't allow the coordinator to schedule the session.
// We chose 2 to approximate the 75% availability requirement for scheduling via self-serve,
// since for most (weekly) recurring sessions we check 6-9 future instances (depending on how
// far in the future the session will start).
const MAX_UNAVAILABLE_DATES = 2;

export type Props = {
  participant: ParticipantRow;
  onHide: () => void;
};

type State = {
  duration: CoachingSessionDuration;
  frequency: CoachingSessionFrequency;
  coachUuid?: string;
  coachTimezone?: string;
  coachName?: string;

  // The selected start date for the session
  startDate: Date;
  // Represents the start time for the session, in PT time, the day/month/year are not used
  startTime: Date;
  recurrenceUntil?: moment.Moment;

  // these fields come from checking the current event definition
  // against a coach's availability
  loadingCoachAvailabilityConflict: boolean;
  coachIsFullyAvailable?: boolean;
  unavailableDates?: string[];
};

export default class CoachingCoordinatorAddSession extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const currentLADate = moment().tz('America/Los_Angeles');
    let startDate = new Date(currentLADate.year(), currentLADate.month(), currentLADate.date());

    const startTime = moment()
      .local()
      .startOf('hour')
      .set({ hour: 16 })
      .toDate();

    this.state = {
      duration: 40,
      startDate: startDate,
      startTime: startTime,
      frequency: 'once',
      loadingCoachAvailabilityConflict: false,
    };
  }

  handleCreateCoachingSession(input: CoachingSessionInput) {
    const { participant } = this.props;

    if (!participant) {
      console.error('Tried to create coaching session without a selected participant');
      ToastActions.showErrorToast(
        'Oops! Looks like you tried to create a coaching session without a selected participant'
      );
      return Promise.resolve();
    }

    return createCoachingSession(
      input,
      [
        {
          query: GET_COACHING_SESSIONS_QUERY,
          variables: {
            userUuid: participant.uuid,
            userType: CoachingUserType.participant,
          },
        },
      ],
      () => ToastActions.showSuccessToast('Created coaching session')
    );
  }

  async componentDidMount() {
    this.trackEvent('view');
  }

  async componentDidUpdate(_prevProps: Props, prevState: State) {
    const fieldsToCheck: (keyof State)[] = [
      'duration',
      'startDate',
      'startTime',
      'frequency',
      'recurrenceUntil',
      'coachUuid',
    ];

    if (fieldsToCheck.some(field => prevState[field] !== this.state[field])) {
      await this.setCoachAvailabilityInfo();
    }
  }

  trackEvent(event: string) {
    EventTracking.trackKey(
      `${CoachingConstants.COORDINATOR_TOOL_EVENT_PREFIX}.add_session.${event}`
    );
  }

  cannotScheduleSession = () => {
    const { loadingCoachAvailabilityConflict, coachUuid, unavailableDates } = this.state;
    return (
      loadingCoachAvailabilityConflict ||
      this.sessionIsInThePast() ||
      !coachUuid ||
      (!!unavailableDates && unavailableDates.length > MAX_UNAVAILABLE_DATES)
    );
  };

  sessionIsInThePast = () => {
    return this.startDateTimeInUtc().isBefore(new Date());
  };

  startDateTimeInUtc = () => {
    const { startDate, startTime } = this.state;
    const startDateTime = moment().tz('America/Los_Angeles');
    startDateTime.set({
      year: startDate.getFullYear(),
      month: startDate.getMonth(),
      date: startDate.getDate(),
      hour: startTime.getHours(),
      minute: startTime.getMinutes(),
      second: startTime.getSeconds(),
      milliseconds: startTime.getMilliseconds(),
    });
    return startDateTime.utc();
  };

  setCoachAvailabilityInfo = throttle(async () => {
    if (!this.state.coachUuid) {
      return;
    }

    const eventDefinition = this.getCurrentEventDefinition(false);
    this.setState({ loadingCoachAvailabilityConflict: true });
    const resp = await getCoachAvailabilityForEvent(
      this.state.coachUuid,
      eventDefinition,
      this.props.participant.uuid
    );
    if (resp) {
      this.trackEvent('coach_unavailabilities.view');
      this.setState({
        coachIsFullyAvailable: resp.coaching?.getCoachAvailabilityForEvent.coachIsFullyAvailable,
        unavailableDates: resp.coaching?.getCoachAvailabilityForEvent.unavailableDates,
      });
    }

    this.setState({ loadingCoachAvailabilityConflict: false });
  }, 200);

  setDuration = (duration: CoachingSessionDuration) => {
    this.trackEvent('duration.change');
    this.setState({ duration });
  };

  setStartDate = (newStartDateTime?: Date) => {
    if (newStartDateTime) {
      this.trackEvent('start_date.change');
      this.setState({
        startDate: newStartDateTime,
      });
    } else {
      ToastActions.showDefaultErrorToast();
    }
  };

  setStartTime = (startTime?: Date) => {
    if (startTime) {
      this.trackEvent('start_time.change');
      this.setState({ startTime: startTime });
    } else {
      ToastActions.showDefaultErrorToast();
    }
  };

  setRecurrenceUntil = (newRecurrenceUntilDate?: Date) => {
    if (newRecurrenceUntilDate) {
      this.trackEvent('end_date.change');
      // set the endDateTime to the last possible moment on the selected date
      // in order to correctly capture all potential coaching sessions
      this.setState({
        recurrenceUntil: moment.utc({
          year: newRecurrenceUntilDate.getUTCFullYear(),
          month: newRecurrenceUntilDate.getUTCMonth(),
          date: newRecurrenceUntilDate.getUTCDate(),
          hour: 23,
          minute: 59,
          second: 59,
          millisecond: 59,
        }),
      });
    } else {
      ToastActions.showDefaultErrorToast();
    }
  };

  setFrequency = (frequency: CoachingSessionFrequency) => {
    this.trackEvent('frequency.change');
    this.setState({ frequency }, () => {
      if (frequency === 'weeklyWithEnd' || frequency === 'everyOtherWeekWithEnd') {
        this.setRecurrenceUntil(this.state.startDate);
        this.trackEvent('end_date.view');
      } else {
        this.setState({ recurrenceUntil: undefined });
      }
    });
  };

  getRecurrenceRules = (excludeUnavailableDates: boolean) => {
    const { frequency, recurrenceUntil, unavailableDates } = this.state;
    if (frequency === 'once') {
      return;
    }

    const isEveryOtherWeek =
      frequency === 'everyOtherWeekOngoing' || frequency === 'everyOtherWeekWithEnd';

    return formatRecurrenceRules({
      interval: isEveryOtherWeek ? 2 : 1,
      until: recurrenceUntil?.toDate(),
      exceptionDates: unavailableDates,
      excludeUnavailableDates,
    });
  };

  getCurrentEventDefinition = (excludeUnavailableDates: boolean) => {
    const { duration } = this.state;
    const startDateTimeString = this.startDateTimeInUtc().toISOString();
    // we can calculate the `endDateTime` of the first session instance
    // by setting the minutes component of the startDateTime to`duration`
    // since we always start at the top of the hour.

    const endDateTime = moment.utc(startDateTimeString).add({ minutes: duration });

    return {
      startDateTime: startDateTimeString,
      endDateTime: endDateTime.toISOString(),
      recurrence: this.getRecurrenceRules(excludeUnavailableDates),
      timezone: this.state.coachTimezone ?? this.props.participant.timezone,
    };
  };

  handleScheduleSessions = async () => {
    this.trackEvent('schedule_session.click');
    const { coachUuid } = this.state;
    if (!coachUuid) {
      console.error('CoachingCoordinatorAddSession - tried to schedule a session without a coach');
      return;
    }

    await this.handleCreateCoachingSession({
      participantUuid: this.props.participant.uuid,
      coachUuid,
      eventDefinition: this.getCurrentEventDefinition(true),
    });
    this.props.onHide();
  };

  renderCoachDropdown = () => {
    return (
      <CoachingCoordinatorCoachSelect
        data-test="coach-dropdown"
        onSelectCoach={(coachItem?: CoachRow) => {
          this.trackEvent('coach.change');
          if (coachItem) {
            this.setState({
              coachUuid: coachItem.uuid,
              coachTimezone: coachItem.timezone ?? undefined,
              coachName: coachItem.name,
            });
          }
        }}
        validStatuses={['Active', 'No auto schedule']}
      />
    );
  };

  renderDurationCoin = (durationValue: CoachingSessionDuration) => {
    const isSelected = this.state.duration === durationValue;
    const onClick = () => this.setDuration(durationValue);
    return (
      <Coin
        onClick={onClick}
        width={32}
        border={isSelected ? 'mascot' : 'percussion'}
        thick={isSelected}
        color="whiteout"
        size="xlarge"
        data-test={`duration-coin-${durationValue}`}>
        <Text lineHeight="flush">{durationValue}</Text>
      </Coin>
    );
  };

  renderDurationField = () => {
    return (
      <React.Fragment>
        <Label>Duration</Label>
        <MediaObject gutter="xsmall" center>
          <MediaObject gutter="xsmall">
            {this.renderDurationCoin(40)}
            {this.renderDurationCoin(60)}
          </MediaObject>
          <Text>min</Text>
        </MediaObject>
      </React.Fragment>
    );
  };

  renderSchedulingErrors = () => {
    const selectedCoachName = this.state.coachName ?? 'The selected coach';
    if (this.sessionIsInThePast()) {
      return (
        <Notice color="paper" noShadow noBorderRadius>
          You cannot schedule sessions in the past.
        </Notice>
      );
    }
    const eventDefinition = this.getCurrentEventDefinition(false);
    return (
      <CoachingCoordinatorAvailabilityConflicts
        coachUuid={this.state.coachUuid}
        coachName={selectedCoachName}
        proposedEventTimeframe={eventDefinition}
        unavailableDates={this.state.unavailableDates}
        coachIsFullyAvailable={this.state.coachIsFullyAvailable}
        loading={this.state.loadingCoachAvailabilityConflict}
        eventFrequency={this.state.frequency}
      />
    );
  };

  render() {
    const { frequency, startDate, startTime, recurrenceUntil } = this.state;
    const frequencyWithEndDate =
      frequency === 'weeklyWithEnd' || frequency === 'everyOtherWeekWithEnd';

    const timezoneLabel = isDSTDay(startDate) ? 'PDT' : 'PST';
    const startTimeLabel = `Start time (${timezoneLabel})`;

    return (
      <BoxBody>
        <Row>
          <Text align="right">
            <Button
              level="icon"
              onClick={() => {
                this.trackEvent('cancel.click');
                this.props.onHide();
              }}>
              <Icon name="x" />
            </Button>
          </Text>
        </Row>
        <Row>
          <Heading level="4">Add session</Heading>
        </Row>
        <Row>{this.renderDurationField()}</Row>
        <Row>
          <Columns2>
            <div>
              <Label>Date</Label>
              <DatePickerInput
                full
                format="ddd, MMMM d"
                min={new Date()}
                max={addDays(new Date(), 60)}
                value={startDate}
                onChange={this.setStartDate}
                data-test="start-date-dropdown"
              />
            </div>
            <div>
              <Label>{startTimeLabel}</Label>
              <TimePickerInput
                data-test="start-time-dropdown"
                full
                step={60}
                value={startTime}
                onChange={this.setStartTime}
              />
            </div>
          </Columns2>
        </Row>
        <Row>
          <Columns2>
            <div>
              <Label>Frequency</Label>
              <Select
                full
                options={CoachingConstants.COACHING_FREQUENCIES_FOR_COORDINATOR}
                onChange={this.setFrequency}
                value={frequency}
                data-test="session-frequency-dropdown"
              />
            </div>
            {frequencyWithEndDate && (
              <div>
                <Label>End date</Label>
                <DatePickerInput
                  full
                  format="ddd, MMMM d"
                  min={startDate}
                  value={recurrenceUntil?.toDate()}
                  onChange={this.setRecurrenceUntil}
                  data-test="recurring-until-dropdown"
                />
              </div>
            )}
          </Columns2>
        </Row>
        <Row>
          <Columns2>
            {this.renderCoachDropdown()}
            <span />
          </Columns2>
        </Row>
        <Row>{this.renderSchedulingErrors()}</Row>
        <Button
          data-test="schedule-sessions-button"
          onClick={this.handleScheduleSessions}
          disabled={this.cannotScheduleSession()}>
          Schedule sessions
        </Button>
      </BoxBody>
    );
  }
}

function isDSTDay(day: Date) {
  const dayInPTTime = moment()
    .tz('America/Los_Angeles')
    .set({
      year: day.getFullYear(),
      month: day.getMonth(),
      day: day.getDate(),
      hour: 16,
    });
  return moment(dayInPTTime).isDST();
}
